if (!customElements.get('newsletter-popup')) {
  customElements.define(
    'newsletter-popup',
    class NewsletterPopup extends HTMLElement {
      constructor() {
        super();
        this.storageKey = this.dataset.storageKey || 'gandini:newsletter-popup';
        this.endpoint = this.dataset.endpoint || '';
        this.trigger = this.dataset.trigger || 'delay';
        this.delay = parseInt(this.dataset.delay, 10) || 5;
        this.scrollPercent = parseInt(this.dataset.scrollPercent, 10) || 40;
        this.frequencyDays = parseInt(this.dataset.frequencyDays, 10) || 7;
        this.fallbackCode = this.dataset.fallbackCode || '';

        this.dialog = this.querySelector('.newsletter-popup__dialog');
        this.form = this.querySelector('.newsletter-popup__form');
        this.formView = this.querySelector('[data-popup-form-view]');
        this.successView = this.querySelector('[data-popup-success-view]');
        this.errorMessage = this.querySelector('[data-popup-error]');
        this.submitButton = this.querySelector('.newsletter-popup__button');
        this.spinner = this.querySelector('.newsletter-popup__spinner');
        this.codeWrapper = this.querySelector('[data-popup-code]');
        this.codeValue = this.querySelector('[data-popup-code-value]');
        this.copyButton = this.querySelector('[data-popup-code-copy]');
        this.phoneInput = this.querySelector('[data-popup-phone]');
      }

      connectedCallback() {
        this.querySelectorAll('[data-popup-close]').forEach((button) => {
          button.addEventListener('click', () => this.close(true));
        });

        this.addEventListener('click', (event) => {
          if (event.target === this) this.close(true);
        });

        this.addEventListener('keyup', (event) => {
          if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close(true);
        });

        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
        if (this.copyButton) this.copyButton.addEventListener('click', this.copyCode.bind(this));
        if (this.phoneInput) this.phoneInput.addEventListener('input', this.maskPhone.bind(this));

        if (window.Shopify && window.Shopify.designMode) {
          this.open();
          return;
        }

        if (!this.shouldDisplay()) return;
        this.scheduleDisplay();
      }

      getState() {
        try {
          return JSON.parse(localStorage.getItem(this.storageKey)) || {};
        } catch (error) {
          return {};
        }
      }

      setState(patch) {
        try {
          localStorage.setItem(this.storageKey, JSON.stringify({ ...this.getState(), ...patch }));
        } catch (error) {
          // Storage unavailable (private mode): fail silently.
        }
      }

      shouldDisplay() {
        const state = this.getState();
        if (state.subscribed) return false;
        if (!state.dismissedAt) return true;

        const elapsedDays = (Date.now() - state.dismissedAt) / 86400000;
        return elapsedDays >= this.frequencyDays;
      }

      scheduleDisplay() {
        if (this.trigger === 'scroll') {
          this.onScroll = () => {
            const scrolled = window.scrollY / (document.body.scrollHeight - window.innerHeight || 1);
            if (scrolled * 100 >= this.scrollPercent) this.open();
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          return;
        }

        if (this.trigger === 'exit_intent') {
          this.onExitIntent = (event) => {
            if (event.clientY <= 0) this.open();
          };
          document.addEventListener('mouseleave', this.onExitIntent);
          // Mobile has no exit intent: fall back to the delay.
          this.timeout = setTimeout(() => this.open(), this.delay * 1000);
          return;
        }

        this.timeout = setTimeout(() => this.open(), this.delay * 1000);
      }

      teardownTriggers() {
        if (this.timeout) clearTimeout(this.timeout);
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.onExitIntent) document.removeEventListener('mouseleave', this.onExitIntent);
      }

      open() {
        if (this.hasAttribute('open')) return;
        this.teardownTriggers();
        this.openedBy = document.activeElement;
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        if (typeof trapFocus === 'function') trapFocus(this, this.dialog);
      }

      close(persist) {
        if (!this.hasAttribute('open') || this.hasAttribute('closing')) return;

        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
        if (persist && !(window.Shopify && window.Shopify.designMode)) this.setState({ dismissedAt: Date.now() });

        this.setAttribute('closing', '');
        setTimeout(() => {
          this.removeAttribute('closing');
          this.removeAttribute('open');
        }, 270);
      }

      maskPhone(event) {
        const digits = event.target.value.replace(/\D/g, '').slice(0, 11);
        if (digits.length === 0) {
          event.target.value = '';
          return;
        }

        let masked = `(${digits.slice(0, 2)}`;
        if (digits.length > 2) masked += `) ${digits.slice(2, 7)}`;
        if (digits.length > 7) masked += `-${digits.slice(7)}`;
        event.target.value = masked;
      }

      showError(message) {
        if (!this.errorMessage) return;
        this.errorMessage.querySelector('[data-popup-error-text]').textContent = message;
        this.errorMessage.removeAttribute('hidden');
      }

      clearError() {
        if (this.errorMessage) this.errorMessage.setAttribute('hidden', '');
      }

      setLoading(loading) {
        if (this.submitButton) this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
        if (this.spinner) this.spinner.toggleAttribute('hidden', !loading);
      }

      async onSubmit(event) {
        event.preventDefault();
        this.clearError();

        if (!this.form.reportValidity()) return;

        const formData = new FormData(this.form);
        const payload = {
          name: (formData.get('name') || '').trim(),
          email: (formData.get('email') || '').trim(),
          phone: (formData.get('phone') || '').trim(),
          shop: window.Shopify ? window.Shopify.shop : window.location.hostname,
          source: 'popup',
          page: window.location.pathname,
        };

        this.setLoading(true);

        try {
          const code = await this.submitLead(payload);
          this.setState({ subscribed: true, subscribedAt: Date.now(), code });
          this.renderSuccess(code);
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setLoading(false);
        }
      }

      async submitLead(payload) {
        if (!this.endpoint) {
          if (this.fallbackCode) return this.fallbackCode;
          throw new Error(this.dataset.errorGeneric);
        }

        let response;
        try {
          response = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } catch (error) {
          if (this.fallbackCode) return this.fallbackCode;
          throw new Error(this.dataset.errorNetwork);
        }

        if (!response.ok) {
          if (this.fallbackCode) return this.fallbackCode;
          throw new Error(this.dataset.errorGeneric);
        }

        const data = await response.json().catch(() => ({}));
        if (data.ok === false) throw new Error(data.error || this.dataset.errorGeneric);

        return data.discount_code || this.fallbackCode;
      }

      renderSuccess(code) {
        if (this.formView) this.formView.setAttribute('hidden', '');
        if (this.successView) this.successView.removeAttribute('hidden');

        if (code && this.codeWrapper && this.codeValue) {
          this.codeValue.textContent = code;
          this.codeWrapper.removeAttribute('hidden');
        }

        if (this.successView) {
          this.successView.setAttribute('tabindex', '-1');
          this.successView.focus();
        }
      }

      copyCode() {
        if (!this.codeValue || !navigator.clipboard) return;
        navigator.clipboard.writeText(this.codeValue.textContent.trim()).then(() => {
          const original = this.copyButton.textContent;
          this.copyButton.textContent = this.dataset.copiedLabel || 'Copiado!';
          setTimeout(() => {
            this.copyButton.textContent = original;
          }, 2000);
        });
      }
    },
  );
}
